/**
 * Upgrade catalog. Sold for coins after each level. Stored per student in the
 * `student_upgrades` table as { upgradeId, level }.
 */
export type UpgradeId =
  | "double-jump"
  | "coin-magnet"
  | "quiz-bonus"
  | "winner-chest"
  | "safety-bubble";

export interface UpgradeDef {
  id: UpgradeId;
  name: string;
  description: string;
  maxLevel: number;
  /** Cost to buy the next level (current level passed in, returns coins). */
  costFor(currentLevel: number): number;
  icon: string;
}

export const UPGRADES: UpgradeDef[] = [
  {
    id: "double-jump",
    name: "Double Jump",
    description: "Jump again in mid-air for tricky obstacles.",
    maxLevel: 1,
    icon: "🦘",
    costFor: () => 120,
  },
  {
    id: "coin-magnet",
    name: "Coin Magnet",
    description: "Pull nearby coins toward you. Bigger radius each level.",
    maxLevel: 3,
    icon: "🧲",
    costFor: (lvl) => [80, 160, 280][lvl] ?? 999,
  },
  {
    id: "quiz-bonus",
    name: "Quiz Bonus",
    description: "Earn extra coins for every correct answer.",
    maxLevel: 3,
    icon: "💡",
    costFor: (lvl) => [60, 120, 240][lvl] ?? 999,
  },
  {
    id: "winner-chest",
    name: "Winner Chest",
    description: "Bonus coins every time you finish a level.",
    maxLevel: 3,
    icon: "🎁",
    costFor: (lvl) => [100, 200, 350][lvl] ?? 999,
  },
  {
    id: "safety-bubble",
    name: "Safety Bubble",
    description: "Longer recovery after a hit so one obstacle costs only one heart.",
    maxLevel: 2,
    icon: "🛡️",
    costFor: (lvl) => [80, 200][lvl] ?? 999,
  },
];

export type UpgradeLevels = Partial<Record<UpgradeId, number>>;

export function levelOf(levels: UpgradeLevels, id: UpgradeId): number {
  return levels[id] ?? 0;
}

/* ── effect resolvers (used by Game) ─────────────────────────────────────── */
export const upgradeEffect = {
  doubleJumpUnlocked: (l: UpgradeLevels) => levelOf(l, "double-jump") > 0,
  /** Coin pickup radius bonus (world units added to the base 0.7). */
  coinMagnetRadius: (l: UpgradeLevels) => levelOf(l, "coin-magnet") * 1.6,
  /** Bonus coins per correct quiz answer. */
  quizBonusCoins: (l: UpgradeLevels) => levelOf(l, "quiz-bonus") * 5,
  /** Extra coins awarded on level completion. */
  winnerChestCoins: (l: UpgradeLevels) => levelOf(l, "winner-chest") * 25,
  /** Extra invulnerability ms after a hit. */
  safetyBubbleMs: (l: UpgradeLevels) => levelOf(l, "safety-bubble") * 500,
};
